// External Libraries
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";

// Material-UI Components
import {
  Stack,
  Chip,
  Button,
  IconButton,
  Typography,
  Container,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  TextField,
  DialogActions,
  DialogTitle,
  DialogContent
} from '@mui/material';

import Grid from '@mui/material/Grid2';

// Internal Components
import MainTable from "../table/MainTable";
import TebraPatients from "./TebraPatients";
import { Box, styled } from "@mui/system";

//Lucide Icons
import { UserPen, RefreshCcw, RefreshCcwDot, RefreshCwOff, UserPlus, Save, CircleSlash, Edit2Icon, CheckCircle } from 'lucide-react';
import CustomDrawer from "../CustomDrawer/CustomDrawer";
import { createRow, MRT_EditActionButtons } from "material-react-table";

/************* Encounters Table *************/

const Encounters = () => {
  const [encounterPatientData, setEncounterPatientData] = useState([]);
  const [query, setQuery] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 1, pageSize: 10, totalPages: 1 });

  const [comparedPatientData, setComparedPatientData] = useState([]);
  const [demoMismatchDialogOpen, setDemoMismatchDialogOpen] = useState(false);

  const [isPatientNotFoundDialogOpen, setPatientNotFoundDialogOpen] = useState(false);
  const [notFoundPatientData, setNotFoundPatientData] = useState([]);


  const [isLoading, setIsLoading] = useState(true);
  const initialLoadComplete = useRef(false);

  const RotatingChip = styled(Chip)(({ theme }) => ({
    '&:hover .rotate-icon': {
      transform: 'rotate(180deg)',
      transition: 'transform 0.65s ease-in-out',
    },
  }));

  const fetchPatients = async (pageIndex, query = '') => {
    try {
      setIsLoading(true);
      const url = `${process.env.REACT_APP_APIS_BASE_URL}/api/encounters?page=${pageIndex}`;
      const response = await axios.get(
          query ? `${process.env.REACT_APP_APIS_BASE_URL}/api/encounters/${query}?page=${pageIndex}` : url,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const patientStatus = response.data.data.data;

      setEncounterPatientData(patientStatus);
      setPagination((prev) => ({
        ...prev,
        pageIndex: response.data.data.current_page,
        totalPages: response.data.data.last_page,
      }));
    } catch (err) {
      console.error("Error fetching patients:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialLoadComplete.current || !isLoading) {
      initialLoadComplete.current = true;
      fetchPatients(pagination.pageIndex, query);
    }
  }, [pagination.pageIndex, query]);

  const handlePageChange = (newPageIndex) => {
    if (typeof newPageIndex === 'object' && newPageIndex.hasOwnProperty('pageIndex')) {
      newPageIndex = newPageIndex.pageIndex;
    }

    if (typeof newPageIndex === 'number' && !isNaN(newPageIndex)) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: newPageIndex + 1,
      }));
    } else {
      console.error("Invalid page index passed:", newPageIndex);
    }
  };

  const handleQueryChange = (queryValue) => {
    setQuery(queryValue);
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  };

  // Patient Not Found Open / Close
  const openPatientNotFoundDrawer = async (patientId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APIS_BASE_URL}/api/get-comparison/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      setNotFoundPatientData(response.data.data);
      setPatientNotFoundDialogOpen(true);
    } catch (err) {
      console.error("Error fetching patient comparison:", err);
    }
  };
  const closePatientNotFoundDrawer = () => {
    setPatientNotFoundDialogOpen(false);
  };

  // Demo Mismatch Open / Close
  const openDemoMismatchDialog = async (patientId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APIS_BASE_URL}/api/get-comparison/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      setComparedPatientData(response.data.data);
      setDemoMismatchDialogOpen(true);
    } catch (err) {
      console.error("Error fetching patient comparison:", err);
    }
  };
  const closeDemomismatchDialog = () => {
    setDemoMismatchDialogOpen(false);
  };
  const getPatientData = (row) => {
    const serviceDate = row.original.Billing_DOS || '';
    const patientId = row.original.Billing_PatientID || '';
    const dateOfBirth = row.original.DemoPatientBirthDate || '';
    const firstName = row.original.DemoPatientFirstName || '';
    const lastName = row.original.DemoPatientLastName || '';
    const status = row.original.status || '';
    return { serviceDate, patientId, dateOfBirth, firstName, lastName, status };
  };
  const handleClick = (status, patientId) => {
    if (status === 'Demo Mismatch' || status === 'null' || status === '') {
      openDemoMismatchDialog(patientId);
    } else if (status === 'Not Found Tebra') {
      openPatientNotFoundDrawer(patientId);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'Demo Mismatch') {
      return <RefreshCcwDot className="rotate-icon" size={16} color="#ffffff" strokeWidth={1.5} />;
    } else if (status === 'Not Found Tebra') {
      return <RefreshCwOff className="rotate-icon" size={16} color="#ffffff" strokeWidth={1.5} />;
    } else if (!status || status === 'null') {
      return <RefreshCcw className="rotate-icon" size={16} color="#ffffff" strokeWidth={1.5} />;
    } else if (status === 'Verified') {
      return <CheckCircle className="rotate-icon" size={16} color="#ffffff" strokeWidth={1.5} />;
    }
    return null;
  };

  const initialGrouping = ['Billing_BillingID'];
  const columns = [
    {
      accessorKey: 'Billing_BillingID',
      header: 'Billing_BillingID',
      size: 300,
      enableHiding: false,
      Cell: ({ row }) => {
        const { serviceDate, patientId, dateOfBirth, firstName, lastName, status } = getPatientData(row);

        return (
          <Box>
            <Stack direction="column" spacing={1} sx={{ marginBottom: '10px' }}>
              <Typography variant="subtitle2" fontWeight="500">
                {firstName} {lastName}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#717171e8' }}>
                {dateOfBirth}
              </Typography>
              <Typography variant="caption" sx={{ color: '#717171e8' }}>
                {patientId} - {serviceDate}
              </Typography>
            </Stack>
            <RotatingChip
              avatar={getStatusIcon(status)}
              label={status || 'Null'}
              variant="contained"
              onClick={['Demo Mismatch', 'null', 'Not Found Tebra', ''].includes(status) ? () => handleClick(status, patientId) : undefined}
              clickable={['Demo Mismatch', 'null', 'Not Found Tebra', ''].includes(status)}
              color={
                status === 'Demo Mismatch' ? 'warning' :
                  status === 'Not Found Tebra' ? 'error' :
                    status === 'Verified' ? 'primary' :
                      !status || status === 'null' ? 'default' : 'success'
              }
              sx={{ marginBottom: '5px', borderRadius: '5px' }}
            />
          </Box>
        );
      },
    },
    {
      accessorKey: 'charge_status',
      header: 'charge_status',
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'DemoPatientFirstName',
      header: 'DemoPatientFirstName',
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'DemoPatientLastName',
      header: 'DemoPatientLastName',
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'DemoPatientBirthDate',
      header: 'DemoPatientBirthDate',
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'Billing_DOS',
      header: 'Billing_DOS',
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'BillingCPTsDateRowAdded',
      header: 'BillingCPTsDateRowAdded',
    },
    {
      accessorKey: 'BillingCPTsID',
      header: 'BillingCPTsID',
      enableGrouping: false,
    },
    {
      accessorKey: 'BillingCPTsSequence',
      header: 'BillingCPTsSequence',
      enableGrouping: false,
    },
    {
      accessorKey: 'BillingCPTsUnits',
      header: 'BillingCPTsUnits',
      enableGrouping: false,
    },
    {
      accessorKey: 'Cpts_CPTCode',
      header: 'Cpts_CPTCode',
      enableGrouping: false,
    },
    {
      accessorKey: 'ICD_Code',
      header: 'ICD_Code',
      enableGrouping: false,
    },
    {
      accessorKey: 'Modifier_Code',
      header: 'Modifier_Code',
      enableGrouping: false,
    },
  ];

  const patientNotFoundColumns = [
    {
      accessorKey: 'PatientID',
      header: 'PatientID',
      Edit: () => null,
    },
    {
      accessorKey: 'FirstName',
      header: 'FirstName',
      Edit: () => (
        <TextField
          label="Tebra Firstname"
          value={notFoundPatientData.acDemographicPatient.FirstName}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'MiddleName',
      header: 'MiddleName',
      Edit: () => (
        <TextField
          label="Tebra Middlename"
          value={notFoundPatientData.acDemographicPatient.MiddleName}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'LastName',
      header: 'LastName',
      Edit: () => (
        <TextField
          label="Tebra Lastname"
          value={notFoundPatientData.acDemographicPatient.LastName}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'PatientFullName',
      header: 'PatientFullName',
      Edit: () => null,
    },
    {
      accessorKey: 'DOB',
      header: 'DOB',
      Edit: () => (
        <TextField
          label="Tebra DOB"
          value={notFoundPatientData.acDemographicPatient.DOB}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'Gender',
      header: 'Gender',
      Edit: () => (
        <TextField
          label="Tebra Gender"
          value={notFoundPatientData.acDemographicPatient.Gender}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'EmailAddress',
      header: 'EmailAddress',
      Edit: () => (
        <TextField
          label="Tebra EmailAddress"
          value={notFoundPatientData.acDemographicPatient.EmailAddress}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'HomePhone',
      header: 'HomePhone',
      Edit: () => (
        <TextField
          label="Tebra HomePhone"
          value={notFoundPatientData.acDemographicPatient.HomePhone}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'WorkPhone',
      header: 'WorkPhone',
      Edit: () => (
        <TextField
          label="Tebra WorkPhone"
          value={notFoundPatientData.acDemographicPatient.WorkPhone}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'AddressLine1',
      header: 'AddressLine1',
      Edit: () => (
        <TextField
          label="Tebra AddressLine1"
          value={notFoundPatientData.acDemographicPatient.AddressLine1}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'City',
      header: 'City',
      Edit: () => (
        <TextField
          label="Tebra City"
          value={notFoundPatientData.acDemographicPatient.City}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'State',
      header: 'State',
      Edit: () => (
        <TextField
          label="Tebra State"
          value={notFoundPatientData.acDemographicPatient.State}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'ZipCode',
      header: 'ZipCode',
      Edit: () => (
        <TextField
          label="Tebra ZipCode"
          value={notFoundPatientData.acDemographicPatient.ZipCode}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'MedicalRecordNumber',
      header: 'MedicalRecordNumber',
      Edit: () => (
        <TextField
          label="Tebra Medical Record Number"
          value={notFoundPatientData.acDemographicPatient.PatientID}
          disabled
          fullWidth
          size="small"
        />
      ),
    },
    {
      accessorKey: 'PrimaryInsurancePolicyCompanyName',
      header: 'PrimaryInsurancePolicyCompanyName',
      Edit: () => null,
    },
    {
      accessorKey: 'PrimaryInsurancePolicyNumber',
      header: 'PrimaryInsurancePolicyNumber',
      Edit: () => null,
    },
    {
      accessorKey: 'SecondaryInsurancePolicyCompanyName',
      header: 'SecondaryInsurancePolicyCompanyName',
      Edit: () => null,
    },
    {
      accessorKey: 'SecondaryInsurancePolicyNumber',
      header: 'SecondaryInsurancePolicyNumber',
      Edit: () => null,
    },
    {
      accessorKey: 'DemoPatientComments',
      header: 'DemoPatientComments',
      Edit: () => null,
    },
  ];

  // Patient Not Found Dialog
  const PatientNotFoundDialog = () => {
    const notFoundPatient = notFoundPatientData?.acDemographicPatient
      ? [notFoundPatientData.acDemographicPatient]
      : [];

    const [isLoadingCreatePatient, setIsLoadingCreatePatient] = useState(false);
    const [isLoadingMapPatient, setIsLoadingMapPatient] = useState(false);

    const renderTopToolbarCustomActions = ({ table }) => (
      <>
        <Button variant="outlined" startIcon={<UserPlus />}
          onClick={() => {
            table.setCreatingRow(
              createRow(table, {
                MedicalRecordNumber: notFoundPatientData.acDemographicPatient?.PatientID || '',
                FirstName: notFoundPatientData.acDemographicPatient?.FirstName || '',
                MiddleName: notFoundPatientData.acDemographicPatient?.MiddleName || '',
                LastName: notFoundPatientData.acDemographicPatient?.LastName || '',
                DOB: notFoundPatientData.acDemographicPatient?.DOB || '',
                Gender: notFoundPatientData.acDemographicPatient?.Gender || '',
                EmailAddress: notFoundPatientData.acDemographicPatient?.EmailAddress || '',
                HomePhone: notFoundPatientData.acDemographicPatient?.HomePhone || '',
                AddressLine1: notFoundPatientData.acDemographicPatient?.AddressLine1 || '',
                City: notFoundPatientData.acDemographicPatient?.City || '',
                State: notFoundPatientData.acDemographicPatient?.State || '',
                ZipCode: notFoundPatientData.acDemographicPatient?.ZipCode || ''
              })
            );
          }}>
          Add as a new patient
        </Button>
      </>
    );

    const renderCreateRowDialogContent = ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle>Create New Patient</DialogTitle>
        <DialogContent sx={{ paddingTop: '20px !important' }}>
          <Stack spacing={3}>
            {internalEditComponents}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    );

    const handleCreateUser = async ({ values, table }) => {
      const acPatientData = notFoundPatientData.acDemographicPatient;
      setIsLoadingCreatePatient(true);

      try {
        if (!acPatientData) {
          console.error('Missing required PatientID for mapping');
          return;
        }

        const patientData = {
          MedicalRecordNumber: acPatientData?.PatientID || '',
          FirstName: acPatientData?.FirstName,
          LastName: acPatientData?.LastName,
          DOB: acPatientData?.DOB,
          Gender: acPatientData?.Gender === 'M' ? 'Male' : acPatientData?.Gender === 'F' ? 'Female' : '',
          EmailAddress: acPatientData?.EmailAddress,
          HomePhone: acPatientData?.HomePhone,
          AddressLine1: acPatientData?.AddressLine1,
          City: acPatientData?.City,
          State: acPatientData?.State,
          ZipCode: acPatientData?.ZipCode
        };

        const response = await axios.post(
          `${process.env.REACT_APP_APIS_BASE_URL}/api/create-patient`,
          patientData,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
          }
        );

        if (response.data.status) {
          console.log('Patient mapped successfully');
          fetchPatients(pagination.pageIndex);
          table.setCreatingRow(false);
        } else {
          console.error(response.data.message || 'Failed to map patient');
        }
      } catch (error) {
        console.error('Error mapping patient:', error);
      } finally {
        setIsLoadingCreatePatient(false)
      }
    };

    const renderRowActionsTebra = ({ row, table }) => {
      return (
        <Box>
          <Button startIcon={<Edit2Icon />} variant="contained" onClick={() => table.setEditingRow(row)}>
            <Typography variant="caption">Map Patient</Typography>
          </Button>
        </Box>
      );
    };

    const mappingPatient = async (table, row) => {
      setIsLoadingMapPatient(true);

      try {
        const tebraTableID = notFoundPatientData.tebraPatient.TebraID;
        console.log(notFoundPatientData.tebraPatient.TebraID, 'test')
        const acPatientID = notFoundPatientData.acDemographicPatient.PatientID;

        if (!acPatientID) {
          console.error('Missing required PatientID for mapping');
          return;
        }

        const response = await axios.post(
          `${process.env.REACT_APP_APIS_BASE_URL}/api/update-patient`,
          {
            TebraID: tebraTableID,
            MedicalRecordNumber: acPatientID
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
          }
        );

        if (response.data.status) {
          console.log('Patient mapped successfully');
          fetchPatients(pagination.pageIndex);
          table.setEditingRow(null);
        } else {
          console.error(response.data.message || 'Failed to map patient');
        }
      } catch (error) {
        console.error('Error mapping patient:', error);
      } finally {
        setIsLoadingMapPatient(false);
      }
    };

    const renderEditRowDialogContent = ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle>Update Patient</DialogTitle>
        <DialogContent sx={{ paddingTop: '20px !important' }}>
          {internalEditComponents}
        </DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    );

    return (
      <Card elevation={0} sx={{ padding: '1.875rem 1.875rem 0' }}>
        <CardContent>
          <Stack spacing={.25}>
            <MainTable
              data={notFoundPatient}
              columns={patientNotFoundColumns}
              title="Patient Not Found"

              // Table behavior and settings
              enableRowSelection={false}
              enableColumnFilters={false}
              enableGlobalFilter={false}
              enablePagination={false}
              enableSorting={true}
              enableGrouping={true}
              paginationDisplayMode="pages"

              // Sticky header and footer
              enableStickyHeader={true}
              enableStickyFooter={true}

              // Column behavior
              enableColumnResizing={false}
              enableHiding={true}

              // Row creation settings
              createDisplayMode="modal"
              onCreatingRowSave={handleCreateUser}
              renderCreateRowDialogContent={renderCreateRowDialogContent}
              isLoading={isLoadingCreatePatient}

              // Pagination settings
              rowsPerPageOptions={[10, 25, 50, 100]}
              initialPageSize={10}

              // Custom actions in the top toolbar
              renderTopToolbarCustomActions={renderTopToolbarCustomActions}

              // Table container styling
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  boxShadow: 'none',
                }
              }}
            />
            <TebraPatients
              notFoundPatientData={notFoundPatientData}
              enableRowActions={true}
              enableEditing={true}
              editDisplayMode="modal"
              onEditingRowSave={mappingPatient}
              renderRowActions={renderRowActionsTebra}
              renderEditRowDialogContent={renderEditRowDialogContent}
              isLoadingMapPatient={isLoadingMapPatient}

              // Row actions settings
              positionActionsColumn="first"
              columnPinning={{ left: ['mrt-row-actions'] }}

              // Column definition options for customization
              displayColumnDefOptions={{
                'mrt-row-actions': {
                  size: 200
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Demo Mismatch Dialog
  const fieldsToCompare = [
    'PatientID',
    'FirstName',
    'MiddleName',
    'LastName',
    'DOB',
    'Gender',
    'EmailAddress',
    'HomePhone',
    'WorkPhone',
    'AddressLine1',
    'City',
    'State',
    'ZipCode',
    'PrimaryInsurancePolicyCompanyName',
    'PrimaryInsurancePolicyNumber',
    'SecondaryInsurancePolicyCompanyName',
    'SecondaryInsurancePolicyNumber'
  ];

  const PatientDataMismatchDialog = () => {
    const [mismatchData, setMismatchData] = useState(comparedPatientData ? [
      { field: 'TebraID', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.TebraID },
      { field: 'PatientID', amazingChartsPatient: comparedPatientData.acDemographicPatient?.PatientID, tebraPatient: comparedPatientData.tebraPatient?.MedicalRecordNumber },
      { field: 'FirstName', amazingChartsPatient: comparedPatientData.acDemographicPatient?.FirstName, tebraPatient: comparedPatientData.tebraPatient?.FirstName },
      { field: 'MiddleName', amazingChartsPatient: comparedPatientData.acDemographicPatient?.MiddleName, tebraPatient: comparedPatientData.tebraPatient?.MiddleName },
      { field: 'LastName', amazingChartsPatient: comparedPatientData.acDemographicPatient?.LastName, tebraPatient: comparedPatientData.tebraPatient?.LastName },
      { field: 'DOB', amazingChartsPatient: comparedPatientData.acDemographicPatient?.DOB, tebraPatient: comparedPatientData.tebraPatient?.DOB },
      { field: 'Gender', amazingChartsPatient: comparedPatientData.acDemographicPatient?.Gender, tebraPatient: comparedPatientData.tebraPatient?.Gender },
      { field: 'EmailAddress', amazingChartsPatient: comparedPatientData.acDemographicPatient?.EmailAddress, tebraPatient: comparedPatientData.tebraPatient?.EmailAddress },
      { field: 'HomePhone', amazingChartsPatient: comparedPatientData.acDemographicPatient?.HomePhone, tebraPatient: comparedPatientData.tebraPatient?.HomePhone },
      { field: 'WorkPhone', amazingChartsPatient: comparedPatientData.acDemographicPatient?.WorkPhone, tebraPatient: comparedPatientData.tebraPatient?.WorkPhone },
      { field: 'AddressLine1', amazingChartsPatient: comparedPatientData.acDemographicPatient?.AddressLine1, tebraPatient: comparedPatientData.tebraPatient?.AddressLine1 },
      { field: 'City', amazingChartsPatient: comparedPatientData.acDemographicPatient?.City, tebraPatient: comparedPatientData.tebraPatient?.City },
      { field: 'State', amazingChartsPatient: comparedPatientData.acDemographicPatient?.State, tebraPatient: comparedPatientData.tebraPatient?.State },
      { field: 'ZipCode', amazingChartsPatient: comparedPatientData.acDemographicPatient?.ZipCode, tebraPatient: comparedPatientData.tebraPatient?.ZipCode },
      { field: 'MedicalRecordNumber', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.MedicalRecordNumber },
      { field: 'PrimaryInsurancePolicyCompanyName', amazingChartsPatient: comparedPatientData.acDemographicPatient?.PrimaryInsurancePolicyCompanyName, tebraPatient: comparedPatientData.tebraPatient?.PrimaryInsurancePolicyCompanyName },
      { field: 'PrimaryInsurancePolicyNumber', amazingChartsPatient: comparedPatientData.acDemographicPatient?.PrimaryInsurancePolicyNumber, tebraPatient: comparedPatientData.tebraPatient?.PrimaryInsurancePolicyNumber },
      { field: 'PrimaryInsurancePolicyCompanyID', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.PrimaryInsurancePolicyCompanyID },
      { field: 'SecondaryInsurancePolicyCompanyName', amazingChartsPatient: comparedPatientData.acDemographicPatient?.SecondaryInsurancePolicyCompanyName, tebraPatient: comparedPatientData.tebraPatient?.SecondaryInsurancePolicyCompanyName },
      { field: 'SecondaryInsurancePolicyNumber', amazingChartsPatient: comparedPatientData.acDemographicPatient?.SecondaryInsurancePolicyNumber, tebraPatient: comparedPatientData.tebraPatient?.SecondaryInsurancePolicyNumber },
      { field: 'SecondaryInsurancePolicyCompanyID', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.SecondaryInsurancePolicyCompanyID },
      { field: 'DefaultCaseID', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.DefaultCaseID },
      { field: 'DefaultCaseName', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.DefaultCaseName },
      { field: 'DefaultCasePayerScenario', amazingChartsPatient: '-', tebraPatient: comparedPatientData.tebraPatient?.DefaultCasePayerScenario },
      { field: 'DemoPatientComments', amazingChartsPatient: comparedPatientData.acDemographicPatient?.DemoPatientComments, tebraPatient: comparedPatientData.tebraPatient?.DemoPatientComments },
    ] : []);

    const [activeEditingRow, setActiveEditingRow] = useState(null);
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [backupData, setBackupData] = useState(null);
    const [isLoadingMismatch, setIsLoadingMismatch] = useState(false);
    const [loadingRowIndex, setLoadingRowIndex] = useState(null);
    const [isLoadingUpdateAll, setIsLoadingUpdateAll] = useState(false);

    const handleSaveSingleChange = async (row) => {
      const updatedField = row.original.field;
      const updatedValue = row.original.amazingChartsPatient;

      try {
        setLoadingRowIndex(row.index);
        setIsLoadingMismatch(true);
        await axios.post(`${process.env.REACT_APP_APIS_BASE_URL}/api/update-patient`, {
          TebraID: comparedPatientData.tebraPatient?.TebraID,
          /* Cases: {
            InsurancePolicies: {
              InsurancePolicy:
              {
                companyID: ;
                companyName: ;
              }
            }
          } */
          [updatedField]: updatedValue,
        });

        const updatedMismatchData = [...mismatchData];
        updatedMismatchData[row.index].tebraPatient = updatedValue;
        setMismatchData(updatedMismatchData);
        setActiveEditingRow(null);
      } catch (error) {
        console.error("Error updating patient data:", error);
      } finally {
        setLoadingRowIndex(null);
        setIsLoadingMismatch(false);
      }
    };

    const handleUpdateAll = () => {
      setBackupData([...mismatchData]);
      const updatedMismatchData = mismatchData.map(row => ({
        ...row,
        tebraPatient: fieldsToCompare.includes(row.field) ? row.amazingChartsPatient : row.tebraPatient
      }));
      setMismatchData(updatedMismatchData);
      setIsBulkUpdating(true);
    };

    const handleSaveAll = async () => {
      try {
        setIsLoadingUpdateAll(true);

        const updates = mismatchData
          .filter(row => fieldsToCompare.includes(row.field))
          .reduce((acc, row) => ({
            ...acc,
            [row.field]: row.amazingChartsPatient
          }), {});

        await axios.post(`${process.env.REACT_APP_APIS_BASE_URL}/api/update-patient`, {
          TebraID: comparedPatientData.tebraPatient?.TebraID,
          ...updates
        });

        setIsBulkUpdating(false);
        setBackupData(null);
      } catch (error) {
        console.error("Error updating patient data:", error);
        setIsLoadingUpdateAll(false);
      } finally {
        setIsLoadingUpdateAll(false);
      }
    };

    const handleCancelAll = () => {
      setMismatchData(backupData);
      setIsBulkUpdating(false);
      setBackupData(null);
    };

    const mismatchColumns = useMemo(() => {

      return [
        {
          accessorKey: 'field',
          header: 'Fields',
        },
        {
          accessorKey: 'amazingChartsPatient',
          header: 'Amazing Charts Patient',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                style={{
                  color:
                    fieldsToCompare.includes(row.original.field) &&
                      row.original.amazingChartsPatient !== row.original.tebraPatient
                      ? '#ff4444'
                      : 'inherit',
                }}
              >
                {row.original.amazingChartsPatient || '-'}
              </span>
            </div>
          ),
        },
        {
          accessorKey: 'tebraPatient',
          header: 'Tebra Patient',
          Cell: ({ row }) => (
            <Stack direction="row" display={'flex'} alignItems={'center'} spacing={2}>
              <span
                style={{
                  color: fieldsToCompare.includes(row.original.field) &&
                    row.original.amazingChartsPatient !== row.original.tebraPatient
                    ? '#ff4444'
                    : 'inherit',
                }}
              >
                {(row.index === activeEditingRow || isBulkUpdating) && fieldsToCompare.includes(row.original.field)
                  ? row.original.amazingChartsPatient
                  : row.original.tebraPatient || '-'}
              </span>
              {!isBulkUpdating && row.index === activeEditingRow ? (
                isLoadingMismatch && loadingRowIndex === row.index ? (
                  <CircularProgress size={24} />
                ) : (
                  <Stack direction="row" spacing={.5}>
                    <Tooltip title="Save Changes">
                      <IconButton
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleSaveSingleChange(row)}
                        sx={{ width: '2rem' }}
                      >
                        <Save />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel Changes">
                      <IconButton
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => setActiveEditingRow(null)}
                        sx={{ width: '2rem' }}
                      >
                        <CircleSlash />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )
              ) : (
                !isBulkUpdating &&
                fieldsToCompare.includes(row.original.field) &&
                row.original.amazingChartsPatient !== row.original.tebraPatient && (
                  <Tooltip title="Update">
                    <IconButton
                      variant="outlined"
                      color="primary"
                      onClick={() => setActiveEditingRow(row.index)}
                      sx={{ width: '2.3rem' }}
                    >
                      <UserPen />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Stack>
          ),
        },
      ];
    }, [activeEditingRow, mismatchData, isBulkUpdating, isLoadingMismatch, loadingRowIndex]);

    return (
      <>
        <Stack direction="row" spacing={2} mb={2} justifyContent="flex-end">
          {!isBulkUpdating ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<UserPen />}
              onClick={handleUpdateAll}
              disabled={!mismatchData.some(row =>
                fieldsToCompare.includes(row.field) &&
                row.amazingChartsPatient !== row.tebraPatient
              )}
            >
              Update All
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={isLoadingUpdateAll ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSaveAll}
                disabled={isLoadingUpdateAll}
              >
                {isLoadingUpdateAll ? 'Saving...' : 'Save All Changes'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CircleSlash />}
                onClick={handleCancelAll}
                disabled={isLoadingUpdateAll}
              >
                Cancel All
              </Button>
            </>
          )}
        </Stack>
        <MainTable
          id="patient-details-table"
          data={mismatchData}
          columns={mismatchColumns}

          // Table behavior and settings
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={false}
          enableDensityToggle={true}
          enableHiding={false}
          enableGlobalFilter={false}
          enableRowSelection={false}

          // Pagination settings
          paginationDisplayMode="pages"

          // Column resizing settings
          enableColumnResizing={false}

          // Styling for the table container (outer wrapper)
          muiTableContainerProps={{
            sx: {
              height: '70vh',
            }
          }}
        />
      </>
    );
  };

  return (
    <>
      <Container maxWidth="xxl" sx={{ padding: '1.875rem' }}>
        <Grid container spacing={2}>
          <Grid size='grow'>
            <Card elevation={0} sx={{ borderRadius: '12px' }}>
              <CardContent>
                <MainTable
                  data={encounterPatientData}
                  columns={columns}
                  title="Encounters"

                  // Table behavior and settings
                  enableRowSelection={false}
                  enableColumnFilters={true}
                  enableGlobalFilter={true}
                  enablePagination={true}
                  enableSorting={true}
                  enableGrouping={true}
                  manualPaginationMode={true}
                  paginationDisplayMode="pages"
                  currentPage={pagination.pageIndex - 1}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}

                  // Column visibility settings
                  columnVisibility={{
                    DemoPatientFirstName: false,
                    DemoPatientLastName: false,
                    DemoPatientBirthDate: false,
                    Billing_DOS: false
                  }}

                  // Sticky headers and footers
                  enableStickyHeader={true}
                  enableStickyFooter={true}

                  // Column resizing and hiding
                  enableColumnResizing={true}
                  enableHiding={true}

                  // Filtering and searching settings
                  manualFiltering={true}
                  onGlobalFilterChange={handleQueryChange}
                  globalFilterValue={query}

                  // Pagination settings
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  initialPageSize={10}

                  // Table grouping settings
                  initialGrouping={initialGrouping}

                  // Styling and customization for the table's paper (outer container)
                  muiTablePaperProps={{
                    sx: {
                      boxShadow: 'none',
                    }
                  }}
                />

                {/* PatientNotFoundDialog */}
                <CustomDrawer
                  drawerTittle='Not Found Tebra Details'
                  openDrawer={isPatientNotFoundDialogOpen}
                  closeDrawer={closePatientNotFoundDrawer}
                  drawerContent={<PatientNotFoundDialog />}
                  variant="temporary"
                  anchor="right"
                  customSx={{ '& .MuiContainer-root': { maxWidth: window.innerWidth } }}
                />

                {/* Demo Mismatch Drawer */}
                <CustomDrawer
                  drawerTittle='Patient Details'
                  openDrawer={demoMismatchDialogOpen}
                  closeDrawer={closeDemomismatchDialog}
                  drawerContent={<PatientDataMismatchDialog />}
                  variant="temporary"
                  anchor="right"
                  customSx={{ '& .MuiContainer-root': { width: window.innerWidth * .5, padding: '15px' } }}
                />

              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Encounters;
